import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererFactory2,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FlyoutService } from './flyout.service';
import { Subscription } from 'rxjs';
import { FlyoutDataType, FlyoutOpeningData, PersonDetails, SessionDetails } from './flyout-models';
import { Logger, LoggerFactory } from '../core/logger.service';
import { MatDialog } from '@angular/material/dialog';
import { WidgetMappingService } from 'app/shared/services/widget-mapping.service';

@Component({
  selector: 'app-flyout',
  templateUrl: './flyout.component.html',
  styleUrls: ['./flyout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutComponent implements OnInit, OnDestroy {
  public flyoutWidth: number;
  public errorOnPersonData = false;
  public errorOnSessionData = false;
  public loadingSessionData = false;
  public loadingPersonData = false;

  private readonly logger: Logger;
  public flyoutOpeningData: FlyoutOpeningData;
  private flyoutSubscription: Subscription;
  public FlyoutDataTypes = FlyoutDataType;
  private sessionDetails: SessionDetails;
  private sessionSubscription: Subscription;
  private session2Subscription: Subscription;
  private personDetails: PersonDetails;
  private personSubscription: Subscription;

  public hasContactGeneralInfo = true;
  public hasContactInformationSection = false;
  public hasSubmittedFormSection = false;
  public hasOnsiteCampaignsSection = false;
  public hasGoalsSection = false;
  public hasDistributedCouponsSection = false;
  public hasTagsSection = false;
  public hasUtmCampaignSection = false;
  public hasActionListSection = false;
  public hasSesionListSection = false;
  public hasDeviceSection = false;
  public hasConsentListSection = false;
  public hasSourceSection = false;
  public hasScoresSection = false;

  public innerWidth: number;
  public isMobile = false;

  private renderer: Renderer2;

  @HostListener('window:resize')
  getScreenHeight(): void {
    this.checkScreenWidth();
  }
  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    // An open menu already handled this Escape (mat-menu prevents default) — close only the menu.
    if (event.defaultPrevented) {
      return;
    }
    if (!this.dialog.openDialogs.length && this.flyoutOpeningData?.type) {
      this.closeFlyout();
    }
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private flyoutService: FlyoutService,
    private dialog: MatDialog,
    rendererFactory: RendererFactory2,
    private readonly widgetMappingService: WidgetMappingService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.logger = loggerFactory.get('FlyoutComponent');
  }

  ngOnInit() {
    this.flyoutSubscription = this.flyoutService.flyoutOpeningDataBS.subscribe(
      (flyoutOpeningData: FlyoutOpeningData) => {
        this.flyoutOpeningData = flyoutOpeningData;
        if (this.flyoutOpeningData) {
          this.flyoutWidth = this.flyoutOpeningData.width;
          this.loadFlyoutData();
        }
      },
    );

    this.sessionSubscription = this.flyoutService.sessionDetailsBS.subscribe((sessionDetails: SessionDetails) => {
      this.sessionDetails = sessionDetails;
      this.processSectionFlags();
    });
    this.personSubscription = this.flyoutService.personDetailsBS.subscribe((personDetails: PersonDetails) => {
      this.personDetails = personDetails;
      this.processSectionFlags();
    });

    this.checkScreenWidth();
  }

  setTransparentBackDrop(value: boolean) {
    if (value) {
      this.renderer.addClass(document.body, 'transparent-backdrop');
    } else {
      this.renderer.removeClass(document.body, 'transparent-backdrop');
    }
  }

  private checkScreenWidth(): void {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth <= 676) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  public scroll(elementId) {
    const el = document.getElementById(elementId);
    el.scrollIntoView();
  }

  private processSectionFlags() {
    let hasOnsiteCampaignsSection = false;
    let hasGoalsSection = false;
    let hasDistributedCouponsSection = false;
    let hasTagsSection = false;
    let hasActionListSection = false;
    let hasUtmCampaignSection = false;
    let hasContactInformationSection = false;
    let hasDeviceSection = false;
    let hasSesionListSection = false;
    let hasConsentListSection = false;
    let hasSourceSection = false;
    let hasSubmittedFormSection = false;

    if (this.flyoutOpeningData) {
      if (this.flyoutOpeningData.type === FlyoutDataType.Session && this.sessionDetails) {
        hasOnsiteCampaignsSection = this.sessionDetails.ocEvents && this.sessionDetails.ocEvents.length > 0;
        hasGoalsSection = this.sessionDetails.goals && this.sessionDetails.goals.length > 0;
        hasDistributedCouponsSection = (this.sessionDetails.distributedCoupons?.length ?? 0) > 0;
        hasActionListSection = this.sessionDetails.actionsList && this.sessionDetails.actionsList.length > 0;
        hasConsentListSection = this.sessionDetails.consents && this.sessionDetails.consents.length > 0;
        hasTagsSection = this.sessionDetails.tags && this.sessionDetails.tags.length > 0;
        hasContactInformationSection = this.shouldShowPersonalDataSection();
        hasDeviceSection = true;
        hasSourceSection = true;
        if (this.flyoutOpeningData.subType === FlyoutDataType.FormResponses) {
          hasSubmittedFormSection = true;
          hasContactInformationSection = false;
          this.setExpandedTab();
        }
      } else if (this.flyoutOpeningData.type === FlyoutDataType.Contact && this.personDetails) {
        hasOnsiteCampaignsSection = this.personDetails.ocEvents && this.personDetails.ocEvents.length > 0;
        hasGoalsSection = this.personDetails.goals && this.personDetails.goals.length > 0;
        hasDistributedCouponsSection =
          this.personDetails.visits?.some((v) => (v.distributedCoupons?.length ?? 0) > 0) ?? false;
        hasUtmCampaignSection = this.personDetails.campaignEvents && this.personDetails.campaignEvents.length > 0;
        hasContactInformationSection = this.shouldShowPersonalDataSection();
        hasTagsSection = true;
        hasDeviceSection = true;
        hasSesionListSection = true;
        hasConsentListSection = this.personDetails.consents && this.personDetails.consents.length > 0;
        this.hasScoresSection = this.personDetails.scores?.length > 0;
      }
    }

    this.hasOnsiteCampaignsSection = hasOnsiteCampaignsSection;
    this.hasGoalsSection = hasGoalsSection;
    this.hasDistributedCouponsSection = hasDistributedCouponsSection;
    this.hasTagsSection = hasTagsSection;
    this.hasActionListSection = hasActionListSection;
    this.hasUtmCampaignSection = hasUtmCampaignSection;
    this.hasContactInformationSection = hasContactInformationSection;
    this.hasDeviceSection = hasDeviceSection;
    this.hasSesionListSection = hasSesionListSection;
    this.hasConsentListSection = hasConsentListSection;
    this.hasSourceSection = hasSourceSection;
    this.hasSubmittedFormSection = hasSubmittedFormSection;
  }

  private shouldShowPersonalDataSection(): boolean {
    if (this.personDetails?.personalData) {
      if (Object.values(this.personDetails?.personalData).filter((x) => !!x).length > 0) {
        return true;
      }
    } else if (this.sessionDetails?.properties) {
      if (Object.values(this.sessionDetails?.properties).filter((x) => !!x).length > 0) {
        return true;
      }
    } else if (this.sessionDetails?.personalData) {
      if (Object.values(this.sessionDetails.personalData).filter((x) => !!x).length > 0) {
        return true;
      }
    }
    return false;
  }

  public closeFlyout() {
    this.flyoutService.close();
  }

  private loadFlyoutData() {
    this.session2Subscription?.unsubscribe();
    if (this.flyoutOpeningData.type === FlyoutDataType.Session) {
      if (
        this.flyoutOpeningData.sessionId &&
        (!this.sessionDetails || this.sessionDetails.id !== this.flyoutOpeningData.sessionId)
      ) {
        this.loadSessionDetails(this.flyoutOpeningData.sessionId);

        // Wait for UID to exist on sessionDetails before loading Person
        this.session2Subscription = this.flyoutService.sessionDetailsBS.subscribe(() => {
          if (this.sessionDetails && this.flyoutOpeningData) {
            this.loadPersonOrOrgDetails();
          }
        });
      }
    } else if (this.flyoutOpeningData.type === FlyoutDataType.Contact) {
      if (this.personDetails) this.flyoutService.personDetailsBS.next(this.personDetails);
      if (
        this.flyoutOpeningData.identifier &&
        (!this.personDetails || this.flyoutOpeningData.identifier !== this.personDetails.personalData.email)
      ) {
        this.loadPersonOrOrgDetails();
      } else if (!this.flyoutOpeningData.identifier && this.flyoutOpeningData.uid) {
        this.loadPersonOrOrgDetails();
      }
    }

    this.setTransparentBackDrop(this.flyoutOpeningData?.transparentBackDrop ?? false);
  }

  private loadPersonOrOrgDetails() {
    this.errorOnPersonData = false;
    this.loadingPersonData = true;
    if (this.flyoutOpeningData.uid && !this.flyoutOpeningData.identifier) {
      this.flyoutService.loadPersonDetails(undefined, this.flyoutOpeningData.uid).subscribe(
        () => {
          this.loadingPersonData = false;
        },
        (error) => {
          this.logger.error('Could not retrieve person for flyout: ' + JSON.stringify(error));
          this.errorOnPersonData = true;
          this.loadingPersonData = false;
        },
      );
    } else {
      const uid = this.sessionDetails ? this.sessionDetails.uid : this.flyoutOpeningData.uid;
      this.flyoutService.loadPersonDetails(this.flyoutOpeningData.identifier, uid).subscribe(
        () => {
          this.loadingPersonData = false;
        },
        (error) => {
          this.logger.error('Could not retrieve person for flyout: ' + JSON.stringify(error));
          this.errorOnPersonData = true;
          this.loadingPersonData = false;
        },
      );
    }
  }

  private loadSessionDetails(sessionId: number) {
    this.errorOnSessionData = false;
    this.loadingSessionData = true;
    try {
      const widgetIdToCampaignIdMap = this.widgetMappingService.getWidgetIdToCampaignIdMap();
      const campaignIdToNameMap = this.widgetMappingService.getCampaignIdToNameMap();
      this.flyoutService.loadSessionDetails(sessionId, widgetIdToCampaignIdMap, campaignIdToNameMap).subscribe(
        () => {
          this.loadingSessionData = false;
        },
        (error) => {
          this.logger.error('Could not retrieve session for flyout: ' + JSON.stringify(error));
          this.errorOnSessionData = true;
          this.loadingSessionData = false;
        },
      );
    } catch (error) {
      // A throw before the subscribe would otherwise leave the spinner running for good.
      this.logger.error('Could not prepare session data for flyout: ' + error);
      this.errorOnSessionData = true;
      this.loadingSessionData = false;
    }
  }

  setExpandedTab() {
    if (this.flyoutOpeningData.subType === FlyoutDataType.FormResponses) {
      this.flyoutOpeningData.expandedTab = 'submitted-form';
    }
  }

  ngOnDestroy(): void {
    [this.flyoutSubscription, this.sessionSubscription, this.session2Subscription, this.personSubscription].forEach(
      (sub) => sub && sub.unsubscribe(),
    );
  }
}
